import React, { useContext, useState, useEffect } from 'react';
import { FormContext, FormSchemaFields } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import FormModal from '../../Shared/form/modal/FormModal';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

interface GroupModalProps {
  show: boolean;
  isEdit: boolean;
  group?: { id: number; name: string; folders: { doc_type_id: number; name: string; sort_order: number }[] } | null;
  onClose: () => void;
  onSubmit: (groupData: { id?: number; name: string; folders: { doc_type_id: number; name: string; sort_order: number }[] }) => Promise<void>;
  onDelete?: (groupId: number) => void;
  availableFolders: { id?: number; name: string }[];
}

const reorder = (list: { value: number; label: string }[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

const GroupModal: React.FC<GroupModalProps> = ({
  show,
  isEdit,
  group,
  onClose,
  onSubmit,
  onDelete,
  availableFolders,
}) => {
  const formContext = useContext(FormContext);
  const [showOrganize, setShowOrganize] = useState(false);
  const [tempOrderedFolders, setTempOrderedFolders] = useState<{ value: number; label: string }[]>([]);

  useEffect(() => {
    if (formContext.value.folders) {
      setTempOrderedFolders(formContext.value.folders);
    }
  }, [formContext.value.folders]);

  if (!show) return null;

  const handleSubmit = async(formData: any) => {
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Group name is required');
      return;
    }

    if (!formData.folders || formData.folders.length === 0) {
      toast.error('Please select at least one folder');

      return;
    }

    formData.folders = [...formData.folders, ...availableFolders.filter(folder=> ['Fixed Details', 'Fixed Details Project','Active Site Maps'].includes(folder.name)).map((folder) => ({
        value: folder.id,
        label: folder.name,
      }))]

    const groupData = {
      ...(isEdit && group ? { id: group.id } : {}),
      name: formData.name.trim(),
      folders: formData.folders.map((folder: any, index: number) => ({
        doc_type_id: folder.value,
        name: folder.label,
        sort_order: index,
      })),
    };

    onSubmit(groupData);
  };

  const handleDelete = () => {
    if (group && onDelete) {
      onDelete(group.id);
    }
  };

  const handleOrganizeClick = () => {
    if (!formContext.value.folders || formContext.value.folders.length === 0) {
      toast.warning('Please select folders first');
      return;
    }
    setTempOrderedFolders(formContext.value.folders);
    setShowOrganize(true);
  };

  const handleOnDragEnd = (result: any) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = reorder(
      tempOrderedFolders,
      result.source.index,
      result.destination.index
    );

    setTempOrderedFolders(reordered);
    formContext.set("folders", reordered);
  };

  const handleRemoveFolder = (folderToRemove: number) => {
    const updated = tempOrderedFolders.filter((folder) => folder.value !== folderToRemove);
    setTempOrderedFolders(updated);
    formContext.set("folders", updated);
  };

  const getSchema = () => ({
    name: {
      label: 'Group Name',
      type: UIControlType.text,
      required: true,
      placeholder: 'Enter group name',
    },
    folders: {
      label: 'Group Folders',
      controlType: UIControlType.customTagSelect,
      isMulti: true,
      showSelectAll: true,
      placeholder: 'Select folders',
      options: availableFolders.filter((folder) => !['Fixed Details', 'Fixed Details Project','Active Site Maps'].includes(folder.name)).map((folder) => ({
        value: folder.id,
        label: folder.name,
      })),
      labelKey: 'label',
      valueKey: 'value',
      validation: {
        required: true,
      },
      reorderEnabled: true,
      onReorderButtonClick: handleOrganizeClick,
    },
  });

  return (
    <FormModal
      modalProps={{
        title: isEdit ? 'Edit Group' : 'Create New Group',
        submitBtnText: 'Submit',
      }}
      onSubmit={(formData)=>handleSubmit(formData)}
      onCancel={onClose}
      showDelete={isEdit}
      onDelete={handleDelete}
    >
      <FormSchemaFields
        schema={getSchema()}
        onChange={formContext.set}
        formData={formContext.value}
        initialFormData={
          isEdit && group
            ?  {
                name: group.name,
                folders: group.folders
                  .map((folder,index) => ({
                    value: folder.doc_type_id,
                    label: folder.name,
                    sort_order: index,
                  })),
              }
            : {}
        }
      />

      {showOrganize && tempOrderedFolders.length > 0 && (
        <div className='organize-folders-section'>
          <h4 className='organize-title'>Document Folders</h4>
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId='groupFolders'>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className='organize-folders-list'
                >
                  {tempOrderedFolders.map((folder, index) => (
                    <Draggable
                      key={`${index}`}
                      draggableId={`${index}`}
                      index={index}
                    >
                      {(provide) => (
                        <div
                          ref={provide.innerRef}
                          {...provide.draggableProps}
                          {...provide.dragHandleProps}
                          className='organize-folder-item'
                          style={{
                            ...provide.draggableProps.style,
                            left: 'auto !important',
                            top: 'auto !important',
                          }}
                        >
                          <FontAwesomeIcon
                            icon='grip-lines'
                            size='sm'
                            className='drag-handle'
                          />
                          <span className='folder-name'>{folder.label}</span>
                          <button
                            className='remove-folder-btn'
                            onClick={() => handleRemoveFolder(folder.value!)}
                            title='Remove'
                            type='button'
                          >
                            <i className='fa fa-trash' aria-hidden='true' />
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
    </FormModal>
  );
};

export default GroupModal;
